import type { ElementType, ElementProperties, TableCell } from '../model/form.types';
import { RADIO_GROUP_DEFAULTS } from '../model/form.types';
import { CHECKBOX_DEFAULTS } from './checkbox-constants';
import { DEFAULT_DATE_FIELD_TYPES } from './date-field-types';
import { getDefaultValues, getLabelElementDefaults } from './element-defaults';
import { TABLE_BORDER_DEFAULTS } from './table-constants';
import TableIcon from '@/shared/assets/icons/TableIcon.svg';
import TextIcon from '@/shared/assets/icons/TextIcon.svg';
import FontIcon from '@/shared/assets/icons/FontIcon.svg';
import TextAlignJustifyIcon from '@/shared/assets/icons/TextAlignJustifyIcon.svg';
import NumberIcon from '@/shared/assets/icons/NumberIcon.svg';
import ArrowDown from '@/shared/assets/icons/ArrowDown.svg';
import CheckboxIcon from '@/shared/assets/icons/CheckboxIcon.svg';
import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
import RadioIcon from '@/shared/assets/icons/RadioIcon.svg';
import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
import MessageIcon from '@/shared/assets/icons/MessageIcon.svg';
import UserIcon from '@/shared/assets/icons/UserIcon.svg';
import PinIcon from '@/shared/assets/icons/PinIcon.svg';
import BookmarkIcon from '@/shared/assets/icons/BookmarkIcon.svg';

type SvgIcon = typeof TableIcon;

export interface ElementRegistryEntry {
  label: string;
  icon: SvgIcon;
  createProperties: (shortId: string) => ElementProperties;
}

function createTableCells(shortId: string, rows: number, columns: number): TableCell[][] {
  const cells: TableCell[][] = [];
  for (let row = 0; row < rows; row++) {
    const rowCells: TableCell[] = [];
    for (let col = 0; col < columns; col++) {
      rowCells.push({
        id: `cell-${shortId}-${row}-${col}`,
        rowIndex: row,
        colIndex: col,
        rowSpan: 1,
        colSpan: 1,
        merged: false,
      });
    }
    cells.push(rowCells);
  }
  return cells;
}

const ELEMENT_REGISTRY: Record<ElementType, ElementRegistryEntry> = {
  TABLE: {
    label: 'Tabela',
    icon: TableIcon,
    createProperties: (shortId) => {
      const rows = 3;
      const columns = 3;
      return {
        name: `table_${shortId}`,
        rows,
        columns,
        is_multiple: false,
        cells: createTableCells(shortId, rows, columns),
        borderWidth: TABLE_BORDER_DEFAULTS.WIDTH,
        borderColor: TABLE_BORDER_DEFAULTS.COLOR,
        borderStyle: TABLE_BORDER_DEFAULTS.STYLE,
        borderPattern: TABLE_BORDER_DEFAULTS.PATTERN,
        tableWidth: 100,
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
        ...getDefaultValues(),
      };
    },
  },
  FIXED_TEXT: {
    label: 'Texto fixo',
    icon: TextIcon,
    createProperties: (shortId) => ({
      name: `fixed_text_${shortId}`,
      content: 'Texto fixo não editável',
      textAlign: 'left' as const,
      fontSize: 10,
      ...getDefaultValues(['font']),
    }),
  },
  TEXT_INPUT: {
    label: 'Campo de texto',
    icon: FontIcon,
    createProperties: (shortId) => ({
      name: `text_input_${shortId}`,
      label: 'Campo de Texto',
      regex: '',
      required: false,
      ...getLabelElementDefaults(),
    }),
  },
  PARAGRAPH: {
    label: 'Parágrafo',
    icon: TextAlignJustifyIcon,
    createProperties: (shortId) => ({
      name: `paragraph_input_${shortId}`,
      label: 'Parágrafo',
      labelPosition: 'top',
      value: '',
      placeholder: '',
      required: false,
      ...getLabelElementDefaults(),
    }),
  },
  NUMBER_INPUT: {
    label: 'Número',
    icon: NumberIcon,
    createProperties: (shortId) => ({
      name: `number_input_${shortId}`,
      label: 'Número',
      ...getLabelElementDefaults(),
    }),
  },
  DROPDOWN: {
    label: 'Dropdown',
    icon: ArrowDown,
    createProperties: (shortId) => ({
      name: `dropdown_${shortId}`,
      label: 'Dropdown',
      alternatives: [{ id: `alt-${shortId}-1`, value: 'Opção 1' }],
      required: false,
      multipleSelection: false,
      labelPosition: 'top' as const,
      ...getLabelElementDefaults(),
    }),
  },
  CHECKBOX: {
    label: 'Checkbox',
    icon: CheckboxIcon,
    createProperties: (shortId) => ({
      name: `checkbox_${shortId}`,
      label: 'Checkbox',
      labelPosition: CHECKBOX_DEFAULTS.LABEL_POSITION,
      orientation: CHECKBOX_DEFAULTS.ORIENTATION,
      options: CHECKBOX_DEFAULTS.OPTIONS.map((opt) => ({ ...opt })),
      required: false,
      ...getLabelElementDefaults(),
    }),
  },
  RADIO_GROUP: {
    label: 'Seleção radial',
    icon: RadioIcon,
    createProperties: (shortId) => ({
      name: `radio_${shortId}`,
      label: 'Seleção radial',
      helperText: '',
      options: [...RADIO_GROUP_DEFAULTS.OPTIONS],
      required: false,
      labelPosition: RADIO_GROUP_DEFAULTS.LABEL_POSITION,
      orientation: RADIO_GROUP_DEFAULTS.ORIENTATION,
      ...getLabelElementDefaults(),
    }),
  },
  DATE_INPUT: {
    label: 'Data',
    icon: CalendarIcon,
    createProperties: (shortId) => ({
      name: `date_input_${shortId}`,
      label: 'Data',
      fieldTypes: [...DEFAULT_DATE_FIELD_TYPES],
      required: false,
      labelPosition: 'top' as const,
      ...getLabelElementDefaults(),
    }),
  },
  EMAIL_INPUT: {
    label: 'E-mail',
    icon: MessageIcon,
    createProperties: (shortId) => ({
      name: `email_input_${shortId}`,
      label: 'E-mail',
      required: false,
      labelPosition: 'top' as const,
      ...getLabelElementDefaults(),
    }),
  },
  URL_INPUT: {
    label: 'URL',
    icon: LinkIcon,
    createProperties: (shortId) => ({
      name: `url_input_${shortId}`,
      label: 'URL',
      required: false,
      labelPosition: 'top' as const,
      ...getLabelElementDefaults(),
    }),
  },
  REGISTER_NUMBER_INPUT: {
    label: 'Matrícula',
    icon: UserIcon,
    createProperties: (shortId) => ({
      name: `register_number_input_${shortId}`,
      label: 'Matrícula',
      required: false,
      labelPosition: 'top' as const,
      ...getLabelElementDefaults(),
    }),
  },
  INDEXER_TITLE: {
    label: 'Título indexador',
    icon: PinIcon,
    createProperties: (shortId) => ({
      name: `indexer_title_${shortId}`,
      label: 'Título indexador',
      indexerType: 'USER_DEFINED' as const,
      labelPosition: 'top' as const,
      color: '#000000',
      ...getLabelElementDefaults(),
    }),
  },
  FILL_TITLE: {
    label: 'Título Preenchimento',
    icon: BookmarkIcon,
    createProperties: (shortId) => ({
      name: `fill_title_${shortId}`,
      label: 'Título preenchimento',
      fillTitleType: 'FIRST_TITLE' as const,
      labelPosition: 'top' as const,
      color: '#000000',
      ...getLabelElementDefaults(),
    }),
  },
  HEADER: {
    label: 'Cabeçalho',
    icon: TextIcon,
    createProperties: (shortId) => ({
      name: `header_${shortId}`,
      label: 'Cabeçalho',
      level: 1 as const,
      fontSize: 12,
      fontWeight: 'semibold' as const,
      fontStyle: 'normal' as const,
      textDecoration: 'none' as const,
      ...getDefaultValues(['font']),
    }),
  },
  IMAGE: {
    label: 'Imagem',
    icon: TextIcon,
    createProperties: (shortId) => ({
      name: `image_${shortId}`,
      label: 'Imagem',
      imageUrl: '',
      allowChange: false,
      ...getDefaultValues(),
    }),
  },
};

export const SIDEBAR_ELEMENT_TYPES: ElementType[] = [
  'TABLE',
  'FIXED_TEXT',
  'TEXT_INPUT',
  'PARAGRAPH',
  'NUMBER_INPUT',
  'DROPDOWN',
  'CHECKBOX',
  'RADIO_GROUP',
  'DATE_INPUT',
  'EMAIL_INPUT',
  'URL_INPUT',
  'REGISTER_NUMBER_INPUT',
  // 'INDEXER_TITLE',
  // 'FILL_TITLE',
];

export function getElementRegistryEntry(type: ElementType): ElementRegistryEntry | null {
  return ELEMENT_REGISTRY[type] ?? null;
}

export function getSidebarElements(): Array<{ type: ElementType; label: string; icon: SvgIcon }> {
  return SIDEBAR_ELEMENT_TYPES.map((type) => {
    const entry = ELEMENT_REGISTRY[type];
    return { type, label: entry.label, icon: entry.icon };
  });
}
